import type { Ref } from 'vue';
import type { ContextMenuItem } from './ContextMenu.vue';

/**
 * Flattened navigation index for keyboard navigation
 */
export interface FlatMenuItem {
    id: string; // Unique identifier (e.g., "0", "0-2", "0-2-1")
    item: ContextMenuItem; // Reference to original item
    level: number; // Nesting depth (0, 1, 2...)
    parentId: string | null; // Parent's id for tree traversal
    indexInParent: number; // Position within siblings array
    path: number[]; // Path from root (e.g., [0, 2, 1])
    isDisabled: boolean; // Computed disabled state
    hasChildren: boolean; // Whether item has submenu
}

/**
 * Navigation state for entire menu system
 */
export interface NavigationState {
    flatIndex: Map<string, FlatMenuItem>; // Fast lookup by ID
    rootIds: string[]; // IDs of top-level items
    childrenMap: Map<string, string[]>; // parentId -> childIds[]
    focusedId: string | null; // Currently focused item
    openSubmenus: Set<string>; // IDs of items with open submenus
}

/**
 * Hover state separate from keyboard navigation
 */
export interface HoverState {
    hoveredPath: string[]; // Path of hovered items (for submenu opening)
    activeTimers: Map<string, number>; // Item ID -> close timer ID
}

/**
 * Composable for data-driven context menu navigation
 */
export function useContextMenuNavigation(navigationState: Ref<NavigationState>) {
    /**
     * Find next non-disabled item in siblings array
     */
    const findNextItem = (fromId: string): FlatMenuItem | null => {
        const current = navigationState.value.flatIndex.get(fromId);
        if (!current) return null;

        // Get siblings at current level
        const siblings = current.parentId
            ? navigationState.value.childrenMap.get(current.parentId) || []
            : navigationState.value.rootIds;

        const currentIndex = siblings.indexOf(fromId);

        // Try next sibling
        for (let i = currentIndex + 1; i < siblings.length; i++) {
            const nextId = siblings[i];
            const nextItem = navigationState.value.flatIndex.get(nextId);
            if (nextItem && !nextItem.isDisabled) {
                return nextItem;
            }
        }

        // Wrap to first sibling
        for (let i = 0; i <= currentIndex; i++) {
            const wrappedId = siblings[i];
            const wrappedItem = navigationState.value.flatIndex.get(wrappedId);
            if (wrappedItem && !wrappedItem.isDisabled && wrappedId !== fromId) {
                return wrappedItem;
            }
        }

        return null;
    };

    /**
     * Find previous non-disabled item in siblings array
     */
    const findPrevItem = (fromId: string): FlatMenuItem | null => {
        const current = navigationState.value.flatIndex.get(fromId);
        if (!current) return null;

        const siblings = current.parentId
            ? navigationState.value.childrenMap.get(current.parentId) || []
            : navigationState.value.rootIds;

        const currentIndex = siblings.indexOf(fromId);

        // Try previous sibling
        for (let i = currentIndex - 1; i >= 0; i--) {
            const prevId = siblings[i];
            const prevItem = navigationState.value.flatIndex.get(prevId);
            if (prevItem && !prevItem.isDisabled) {
                return prevItem;
            }
        }

        // Wrap to last sibling
        for (let i = siblings.length - 1; i >= currentIndex; i--) {
            const wrappedId = siblings[i];
            const wrappedItem = navigationState.value.flatIndex.get(wrappedId);
            if (wrappedItem && !wrappedItem.isDisabled && wrappedId !== fromId) {
                return wrappedItem;
            }
        }

        return null;
    };

    /**
     * Navigate into submenu (first non-disabled child)
     */
    const navigateIntoSubmenu = (itemId: string): FlatMenuItem | null => {
        const childIds = navigationState.value.childrenMap.get(itemId) || [];

        for (const childId of childIds) {
            const childItem = navigationState.value.flatIndex.get(childId);
            if (childItem && !childItem.isDisabled) {
                return childItem;
            }
        }

        return null;
    };

    /**
     * Navigate to parent item
     */
    const navigateToParent = (itemId: string): FlatMenuItem | null => {
        const current = navigationState.value.flatIndex.get(itemId);
        if (!current?.parentId) return null;

        return navigationState.value.flatIndex.get(current.parentId) || null;
    };

    /**
     * Find first non-disabled item in current level
     */
    const findFirstInLevel = (itemId: string): FlatMenuItem | null => {
        const current = navigationState.value.flatIndex.get(itemId);
        if (!current) return null;

        const siblings = current.parentId
            ? navigationState.value.childrenMap.get(current.parentId) || []
            : navigationState.value.rootIds;

        for (const siblingId of siblings) {
            const item = navigationState.value.flatIndex.get(siblingId);
            if (item && !item.isDisabled) {
                return item;
            }
        }

        return null;
    };

    /**
     * Find last non-disabled item in current level
     */
    const findLastInLevel = (itemId: string): FlatMenuItem | null => {
        const current = navigationState.value.flatIndex.get(itemId);
        if (!current) return null;

        const siblings = current.parentId
            ? navigationState.value.childrenMap.get(current.parentId) || []
            : navigationState.value.rootIds;

        for (let i = siblings.length - 1; i >= 0; i--) {
            const siblingId = siblings[i];
            const item = navigationState.value.flatIndex.get(siblingId);
            if (item && !item.isDisabled) {
                return item;
            }
        }

        return null;
    };

    /**
     * Focus DOM element by item ID
     * Search globally since submenus are teleported to body
     */
    const focusItemById = (itemId: string): boolean => {
        const element = document.querySelector(`[data-item-id="${itemId}"]`) as HTMLElement;

        if (element && !element.classList.contains('ui-context-menu-item--disabled')) {
            element.focus();
            return true;
        }

        return false;
    };

    /**
     * Build ancestor path for an item (including the item itself)
     */
    const buildAncestorPath = (itemId: string): string[] => {
        const path: string[] = [];
        let currentId: string | null = itemId;

        while (currentId) {
            path.unshift(currentId);
            const current = navigationState.value.flatIndex.get(currentId);
            currentId = current?.parentId || null;
        }

        return path;
    };

    return {
        findNextItem,
        findPrevItem,
        navigateIntoSubmenu,
        navigateToParent,
        findFirstInLevel,
        findLastInLevel,
        focusItemById,
        buildAncestorPath,
    };
}
